import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { ButtonWrapper } from 'cstar-react-primitives/lib/redux-form/buttons/ButtonWrapper';

import { addressFieldNames } from '../address/addressModule';
import { SENDER_EMAIL_FIELD } from '../payment/paymentConstants';

import {
    getPaidCardsCount,
    getSubTotal,
    getShippingTotal,
    hasPlasticItems,
    getUYOPQuantity,
    getItemsAccessoryCount,
    getItemAccessoriesGrandTotalCost
} from '../item/itemSelectors';
import {
    getDiscountRewardAmount
} from '../promo/promoSelectors';

import applePayMessages from './applePayMessages';
import { getApplePayPaymentRequest, startApplePaySession } from './applePay';
import { completePayment, fetchMerchantSession } from './applePayModule';
import itemMessages from '../item/itemMessages';
import { getBrandName } from '../brand/brandSelectors';
import {
    getActivationFeesCount,
    getActivationFeesTotal,
    getGrandTotal,
    getUyopFeesTotal
} from '../payment/paymentSelectors';

export const ApplePayButton = ({
    intl,
    hasPlastic,
    paidCardsCount,
    subTotal,
    shippingTotal,
    grandTotalCost,
    validationCallback,
    authorizationCallback,
    uyoPlasticFeesTotal,
    uyoPlasticCount,
    activationFeesTotal,
    activationFeesCardsCount,
    accessoriesCount,
    itemAccessoriesTotal,
    discountRewardAmount,
    isDisabled,
    handleSubmit,
    brandName
}) => {
    const paymentRequestProps = {
        countryCode: 'US',
        currencyCode: 'USD',
        items: [
            {
                label: intl.formatMessage(applePayMessages.cardSubtotalLineItem, { count: paidCardsCount }),
                cost: subTotal
            }
        ],
        totalLabel: intl.formatMessage(applePayMessages.totalAmountLineItem, { brandName }),
        totalCost: grandTotalCost
    };

    const startSession = () => {
        const paymentRequest = getApplePayPaymentRequest(paymentRequestProps);
        startApplePaySession(paymentRequest, validationCallback, authorizationCallback({
            [addressFieldNames.ADDRESS_FIRSTNAME]: intl.formatMessage(applePayMessages.firstNameRequired),
            [addressFieldNames.ADDRESS_LASTNAME]: intl.formatMessage(applePayMessages.lastNameRequired),
            [addressFieldNames.ADDRESS_CITY]: intl.formatMessage(applePayMessages.cityRequired),
            [addressFieldNames.ADDRESS_STATE]: intl.formatMessage(applePayMessages.stateRequired),
            [addressFieldNames.ADDRESS_POSTCODE]: intl.formatMessage(applePayMessages.postCodeRequired),
            [addressFieldNames.ADDRESS_PHONE]: intl.formatMessage(applePayMessages.phoneRequired),
            [SENDER_EMAIL_FIELD]: intl.formatMessage(applePayMessages.senderEmailRequired),
            [addressFieldNames.ADDRESS_STREET1]: intl.formatMessage(applePayMessages.street1Required)
        }));
    };

    if (accessoriesCount > 0) {
        paymentRequestProps.items.push({
            label: intl.formatMessage(itemMessages.orderTotalAccessories, { count: accessoriesCount }),
            cost: itemAccessoriesTotal
        });
    }

    if (hasPlastic) {
        paymentRequestProps.items.push({
            label: intl.formatMessage(applePayMessages.shippingLineItem),
            cost: shippingTotal
        });
    }

    if (uyoPlasticFeesTotal) {
        paymentRequestProps.items.push({
            label: intl.formatMessage(itemMessages.orderTotalUYOPlasticFee, { count: uyoPlasticCount }),
            cost: uyoPlasticFeesTotal
        });
    }

    if (activationFeesCardsCount) {
        paymentRequestProps.items.push({
            label: intl.formatMessage(itemMessages.orderTotalActivationFees, { count: activationFeesCardsCount }),
            cost: activationFeesTotal
        });
    }

    if (discountRewardAmount < 0) {
        paymentRequestProps.items.push({
            label: intl.formatMessage(applePayMessages.discountLineItem),
            cost: discountRewardAmount
        });
    }

    const btnProps = {
        btnType: 'button',
        cssClass: 'apple-pay-btn',
        cashbotName: 'pay-with-apple-pay',
        onClick: isDisabled ? handleSubmit(() => false) : handleSubmit(startSession)
    };

    return (
        <ButtonWrapper {...btnProps}>
            <FormattedMessage {...applePayMessages.payWithApplePay} />
        </ButtonWrapper>
    );
};

ApplePayButton.defaultProps = {
    hasPlastic: false,
    isDisabled: false
};

ApplePayButton.propTypes = {
    intl: intlShape.isRequired,
    hasPlastic: PropTypes.bool,
    paidCardsCount: PropTypes.number.isRequired,
    subTotal: PropTypes.number.isRequired,
    shippingTotal: PropTypes.number.isRequired,
    grandTotalCost: PropTypes.number.isRequired,
    validationCallback: PropTypes.func.isRequired,
    authorizationCallback: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    discountRewardAmount: PropTypes.number.isRequired,
    uyoPlasticFeesTotal: PropTypes.number.isRequired,
    uyoPlasticCount: PropTypes.number.isRequired,
    activationFeesTotal: PropTypes.number.isRequired,
    activationFeesCardsCount: PropTypes.number.isRequired,
    accessoriesCount: PropTypes.number.isRequired,
    itemAccessoriesTotal: PropTypes.number.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    brandName: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
    hasPlastic: hasPlasticItems(state),
    paidCardsCount: getPaidCardsCount(state),
    subTotal: parseFloat(getSubTotal(state).toFixed(2)),
    shippingTotal: getShippingTotal(state),
    grandTotalCost: parseFloat(getGrandTotal(state).toFixed(2)),
    uyoPlasticFeesTotal: parseFloat(getUyopFeesTotal(state).toFixed(2)),
    uyoPlasticCount: getUYOPQuantity(state),
    activationFeesTotal: getActivationFeesTotal(state),
    activationFeesCardsCount: getActivationFeesCount(state),
    accessoriesCount: getItemsAccessoryCount(state),
    itemAccessoriesTotal: parseFloat(getItemAccessoriesGrandTotalCost(state).toFixed(2)),
    discountRewardAmount: getDiscountRewardAmount(state),
    brandName: getBrandName(state)
});

const mapDispatchToProps = dispatch => ({
    validationCallback: validateMerchantEvent => dispatch(fetchMerchantSession(validateMerchantEvent)),
    authorizationCallback: messages => paymentEvent => dispatch(completePayment(paymentEvent, messages))
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ApplePayButton));
